import { cn } from '@/utils/cn'

import type {
  ExtendedColumnDef,
  ResponsiveConfig,
  ScreenSize,
  TableDensity
} from './DataTable.types'

// Density class mappings
export const densityClasses: Record<TableDensity, string> = {
  compact: '[&_td]:py-1 [&_th]:py-1 text-xs',
  normal: '[&_td]:py-3 [&_th]:py-3 text-sm',
  comfortable: '[&_td]:py-4 [&_th]:py-4 text-base'
}

// Get current screen size
export const getScreenSize = (): ScreenSize => {
  if (typeof window === 'undefined') return 'desktop'

  const width = window.innerWidth
  if (width < 768) return 'mobile'
  if (width < 1024) return 'tablet'
  return 'desktop'
}

// Debounce utility function
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout | null = null

  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}

// Filter visible columns based on responsive config
export const filterVisibleColumns = <TData>(
  columns: ExtendedColumnDef<TData>[],
  screenSize: ScreenSize,
  config?: ResponsiveConfig
): ExtendedColumnDef<TData>[] => {
  if (!config) return columns

  return columns.filter((column) => {
    const meta = column.meta
    // const columnId = column.id as string

    // Check specific hide rules
    const hideColumns = config.hideColumns
    if (hideColumns) {
      const columnIdStr = column.id || (column as any).accessorKey
      if (screenSize === 'mobile' && hideColumns.mobile?.includes(columnIdStr)) return false
      if (screenSize === 'tablet' && hideColumns.tablet?.includes(columnIdStr)) return false
    }

    // Check meta flags
    if (screenSize === 'mobile' && meta?.hideOnMobile) return false
    if (screenSize === 'tablet' && meta?.hideOnTablet) return false

    // Check priority-based hiding
    const priority = meta?.priority ?? 999
    const priorityThreshold = {
      mobile: 1,
      tablet: 2,
      desktop: 999
    }[screenSize]

    return priority <= priorityThreshold
  })
}

// Get column alignment class
export const getAlignmentClass = (align?: 'left' | 'center' | 'right'): string => {
  switch (align) {
    case 'center':
      return 'text-center'
    case 'right':
      return 'text-right'
    default:
      return 'text-left'
  }
}

// Get sticky column classes
export const getStickyColumnClass = (sticky?: 'left' | 'right') => {
  if (!sticky) return ''
  return cn(
    'sticky z-40 bg-background/95 backdrop-blur-md',
    // Forzar el borde bottom para que se vea la línea divisoria de las filas
    '!border-b !border-border',
    // Bordes laterales para separación visual
    sticky === 'left' && '!border-r-2 !border-r-border',
    sticky === 'right' && '!border-l-2 !border-l-border'
  )
}

// Get sticky column inline styles for shadow effects
export const getStickyColumnStyles = (sticky?: 'left' | 'right') => {
  if (!sticky) return {}

  const baseStyles = {
    [sticky]: 0
  }

  // Agregar sombra y borde bottom para mayor visibilidad
  if (sticky === 'left') {
    return {
      ...baseStyles,
      boxShadow: '2px 0 8px -2px rgba(0, 0, 0, 0.2), 1px 0 0 0 rgba(0, 0, 0, 0.1)',
      borderBottom: '1px solid hsl(var(--border))'
    }
  } else {
    return {
      ...baseStyles,
      boxShadow: '-2px 0 8px -2px rgba(0, 0, 0, 0.2), -1px 0 0 0 rgba(0, 0, 0, 0.1)',
      borderBottom: '1px solid hsl(var(--border))'
    }
  }
}

// Generate pagination numbers
export const getPaginationNumbers = (
  currentPage: number,
  totalPages: number,
  maxVisible: number = 5
): (number | '...')[] => {
  if (totalPages <= maxVisible) {
    return Array.from({ length: totalPages }, (_, i) => i)
  }

  const half = Math.floor(maxVisible / 2)
  let start = Math.max(0, currentPage - half)
  const end = Math.min(totalPages - 1, start + maxVisible - 1)

  if (end - start + 1 < maxVisible) {
    start = Math.max(0, end - maxVisible + 1)
  }

  const pages: (number | '...')[] = []

  if (start > 0) {
    pages.push(0)
    if (start > 1) pages.push('...')
  }

  for (let i = start; i <= end; i++) {
    pages.push(i)
  }

  if (end < totalPages - 1) {
    if (end < totalPages - 2) pages.push('...')
    pages.push(totalPages - 1)
  }

  return pages
}

// Format file size
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

// Generate unique ID
export const generateId = (): string => {
  return Math.random().toString(36).substring(2, 9)
}

// Export utilities
export const exportToCSV = <TData>(
  data: TData[],
  columns: ExtendedColumnDef<TData>[],
  filename: string = 'export'
): void => {
  // Filter exportable columns
  const exportableColumns = columns.filter((col) => col.meta?.exportable !== false)

  // Create CSV headers
  const headers = exportableColumns
    .map((col) => {
      if (typeof (col as any).header === 'string') return (col as any).header
      if ((col as any).id) return (col as any).id
      return String((col as any).accessorKey || '')
    })
    .join(',')

  // Create CSV rows
  const csvRows = data.map((row) => {
    return exportableColumns
      .map((col) => {
        const value = (col as any).accessorFn
          ? (col as any).accessorFn(row, 0)
          : (col as any).accessorKey
            ? (row as any)[(col as any).accessorKey]
            : ''

        // Escape CSV value
        const stringValue = String(value || '')
        return stringValue.includes(',') || stringValue.includes('"')
          ? `"${stringValue.replace(/"/g, '""')}"`
          : stringValue
      })
      .join(',')
  })

  // Combine headers and rows
  const csvContent = [headers, ...csvRows].join('\n')

  // Create and download file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `${filename}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }
}

// Placeholder for Excel export (would need xlsx library)
export const exportToExcel = <TData>(
  data: TData[],
  columns: ExtendedColumnDef<TData>[],
  filename: string = 'export'
): void => {
  console.warn('Excel export requires xlsx library. Falling back to CSV.')
  exportToCSV(data, columns, filename)
}

// Placeholder for PDF export (would need jsPDF library)
export const exportToPDF = <TData>(
  data: TData[],
  columns: ExtendedColumnDef<TData>[],
  filename: string = 'export'
): void => {
  console.warn('PDF export requires jsPDF library. Falling back to CSV.')
  exportToCSV(data, columns, filename)
}

// Deep merge utility for options
export const deepMerge = <T extends Record<string, unknown>>(target: T, source: Partial<T>): T => {
  const result = { ...target }

  for (const key in source) {
    const sourceValue = source[key]
    const targetValue = result[key]
    if (
      sourceValue &&
      typeof sourceValue === 'object' &&
      !Array.isArray(sourceValue) &&
      targetValue &&
      typeof targetValue === 'object' &&
      !Array.isArray(targetValue)
    ) {
      result[key] = deepMerge(
        targetValue as Record<string, unknown>,
        sourceValue as Record<string, unknown>
      ) as T[typeof key]
    } else if (sourceValue !== undefined) {
      result[key] = sourceValue as T[typeof key]
    }
  }

  return result
}

// Calculate table stats
export const calculateTableStats = <TData>(
  data: TData[],
  pageSize: number,
  currentPage: number
): {
  total: number
  start: number
  end: number
  hasNext: boolean
  hasPrevious: boolean
} => {
  const total = data.length
  const start = currentPage * pageSize + 1
  const end = Math.min((currentPage + 1) * pageSize, total)
  const hasNext = end < total
  const hasPrevious = currentPage > 0

  return { total, start, end, hasNext, hasPrevious }
}

// Validate column configuration
export const validateColumns = <TData>(columns: ExtendedColumnDef<TData>[]): string[] => {
  const errors: string[] = []

  columns.forEach((column, index) => {
    if (!column) {
      errors.push(`Column at index ${index} is undefined or null`)
      return
    }
    // const accessorKey = (column as any).accessorKey
    const accessorKey = (column as Partial<{ accessorKey: string }>).accessorKey
    if (!column.id && !accessorKey) {
      errors.push(`Column at index ${index} must have either 'id' or 'accessorKey'`)
    }

    if (column.meta?.width && typeof column.meta.width === 'string') {
      if (!column.meta.width.includes('px') && !column.meta.width.includes('%')) {
        errors.push(`Column ${column.id || index} width must include units (px or %)`)
      }
    }

    if (column.meta?.priority && (column.meta.priority < 1 || column.meta.priority > 10)) {
      errors.push(`Column ${column.id || index} priority must be between 1 and 10`)
    }
  })

  return errors
}

// Performance utilities
export const memoizeColumns = <TData>(
  columns: ExtendedColumnDef<TData>[]
): ExtendedColumnDef<TData>[] => {
  // No-op memoization: just return the columns as-is to preserve type safety
  return columns
}

// Accessibility helpers
export const getAriaLabel = (action: string, context?: string): string => {
  return context ? `${action} ${context}` : action
}

export const getAriaSort = (
  direction: false | 'asc' | 'desc'
): 'none' | 'ascending' | 'descending' => {
  if (direction === 'asc') return 'ascending'
  if (direction === 'desc') return 'descending'
  return 'none'
}
